<script setup>
import { ref, computed, onMounted } from 'vue';
import ContactsAPI from 'dashboard/api/contacts';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  // Si true, devuelve números de teléfono en lugar de IDs
  returnPhoneNumbers: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const selectedContacts = ref([]);
let searchTimeout = null;

const contactKey = contact =>
  props.returnPhoneNumbers ? contact.phone_number : String(contact.id);

// Actualizar modelValue con IDs o números según configuración
const updateModelValue = () => {
  if (props.returnPhoneNumbers) {
    const phones = [...new Set(selectedContacts.value.map(c => c.phone_number))]
      .filter(Boolean)
      .join('\n');
    emit('update:modelValue', phones);
  } else {
    const idsArray = [...new Set(selectedContacts.value.map(c => c.id))]
      .filter(Boolean)
      .sort((a, b) => a - b);
    emit('update:modelValue', idsArray.join(','));
  }
};

const initializeSelection = () => {
  if (!props.returnPhoneNumbers || !props.modelValue) return;

  const phones = props.modelValue
    .split(/[\n,]/)
    .map(phone => phone.trim())
    .filter(Boolean);

  selectedContacts.value = phones.map((phone, index) => ({
    id: `prefill-${index}`,
    name: phone,
    phone_number: phone,
  }));
};

const searchContacts = async () => {
  const query = searchQuery.value.trim();
  if (query.length < 2) {
    searchResults.value = [];
    return;
  }

  isSearching.value = true;
  try {
    const response = await ContactsAPI.search(query, 1, 'name', '');
    searchResults.value = (response.data.payload || []).filter(
      contact => contact.phone_number
    );
  } catch {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const handleSearchInput = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    searchContacts();
  }, 300);
};

// Contador de seleccionados
const selectedCount = computed(() => selectedContacts.value.length);

// Verificar si un contacto está seleccionado
const isSelected = contact =>
  selectedContacts.value.some(c => contactKey(c) === contactKey(contact));

// Toggle selección de contacto
const toggleContact = contact => {
  const key = contactKey(contact);
  const exists = selectedContacts.value.some(c => contactKey(c) === key);

  if (exists) {
    selectedContacts.value = selectedContacts.value.filter(
      c => contactKey(c) !== key
    );
  } else {
    selectedContacts.value = [...selectedContacts.value, contact];
  }
  updateModelValue();
};

const removeSelectedContact = contact => {
  const key = contactKey(contact);
  selectedContacts.value = selectedContacts.value.filter(
    c => contactKey(c) !== key
  );
  updateModelValue();
};

// Seleccionar todos los contactos visibles
const selectAll = () => {
  const selectedKeys = new Set(selectedContacts.value.map(c => contactKey(c)));
  const next = [...selectedContacts.value];

  searchResults.value.forEach(contact => {
    const key = contactKey(contact);
    if (!selectedKeys.has(key)) next.push(contact);
  });

  selectedContacts.value = next;
  updateModelValue();
};

// Deseleccionar todos
const clearAll = () => {
  selectedContacts.value = [];
  updateModelValue();
};

onMounted(() => {
  initializeSelection();
});
</script>

<template>
  <div class="space-y-3">
    <!-- Buscador -->
    <Input
      v-model="searchQuery"
      :placeholder="$t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.SEARCH_PLACEHOLDER')"
      icon-left="i-lucide-search"
      @input="handleSearchInput"
    />

    <!-- Acciones -->
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2 text-sm text-n-slate-11">
        <Icon icon="i-lucide-users" class="size-4" />
        <span>
          {{
            $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.SELECTED_COUNT', {
              count: selectedCount,
            })
          }}
        </span>
      </div>

      <div class="flex items-center gap-2">
        <button
          type="button"
          class="text-sm text-n-blue-11 hover:text-n-blue-12 font-medium"
          @click="selectAll"
        >
          {{ $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.SELECT_ALL') }}
        </button>
        <span class="text-n-slate-8">|</span>
        <button
          type="button"
          class="text-sm text-n-red-11 hover:text-n-red-12 font-medium"
          @click="clearAll"
        >
          {{ $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.CLEAR_ALL') }}
        </button>
      </div>
    </div>

    <div
      v-if="selectedCount > 0"
      class="border border-n-weak rounded-lg bg-n-alpha-2 p-3 space-y-2"
    >
      <p class="text-xs font-medium text-n-slate-12">
        {{ $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.SELECTED_TITLE') }}
      </p>

      <div class="max-h-36 overflow-y-auto space-y-2">
        <div
          v-for="contact in selectedContacts"
          :key="contactKey(contact)"
          class="flex items-center justify-between gap-2 rounded-md bg-n-alpha-3 border border-n-weak px-2.5 py-2"
        >
          <div class="min-w-0">
            <p class="text-xs font-medium text-n-slate-12 truncate">
              {{
                contact.name || $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.UNNAMED')
              }}
            </p>
            <p class="text-xs text-n-slate-11 truncate">
              {{ contact.phone_number }}
            </p>
          </div>

          <button
            type="button"
            class="text-xs text-n-red-11 hover:text-n-red-12 font-medium"
            @click="removeSelectedContact(contact)"
          >
            {{ $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.REMOVE') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Lista de contactos -->
    <div
      v-if="isSearching"
      class="flex items-center justify-center p-8 text-n-slate-11"
    >
      <Icon icon="i-lucide-loader-2" class="size-5 animate-spin mr-2" />
      {{ $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.LOADING') }}
    </div>

    <div
      v-else-if="searchQuery.trim().length < 2"
      class="flex flex-col items-center justify-center p-8 text-center text-n-slate-11"
    >
      <Icon icon="i-lucide-search" class="size-12 mb-2 opacity-50" />
      <p class="text-sm">
        {{ $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.SEARCH_HELP') }}
      </p>
    </div>

    <div
      v-else-if="searchResults.length === 0"
      class="flex flex-col items-center justify-center p-8 text-center text-n-slate-11"
    >
      <Icon icon="i-lucide-user-x" class="size-12 mb-2 opacity-50" />
      <p class="text-sm">
        {{ $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.NO_RESULTS') }}
      </p>
    </div>

    <div
      v-else
      class="border border-n-weak rounded-lg divide-y divide-n-weak max-h-80 overflow-y-auto"
    >
      <label
        v-for="contact in searchResults"
        :key="contactKey(contact)"
        class="flex items-center gap-3 p-3 hover:bg-n-alpha-3 cursor-pointer transition-colors"
      >
        <input
          type="checkbox"
          :checked="isSelected(contact)"
          class="size-4 rounded border-n-slate-7 text-n-blue-9 focus:ring-2 focus:ring-n-blue-8 cursor-pointer"
          @change="toggleContact(contact)"
        />

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{
                contact.name || $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.UNNAMED')
              }}
            </p>
            <span class="text-xs text-n-slate-11 font-mono flex-shrink-0">
              #{{ contact.id }}
            </span>
          </div>
          <div class="flex items-center gap-2 mt-0.5">
            <Icon
              icon="i-lucide-phone"
              class="size-3 text-n-slate-11 flex-shrink-0"
            />
            <p class="text-xs text-n-slate-11 font-mono truncate">
              {{ contact.phone_number }}
            </p>
          </div>
          <p
            v-if="contact.email"
            class="text-xs text-n-slate-11 truncate mt-0.5"
          >
            {{ contact.email }}
          </p>
        </div>
      </label>
    </div>

    <!-- Ayuda -->
    <div
      class="text-xs text-n-slate-11 bg-n-blue-3 border border-n-blue-6 rounded-lg p-3"
    >
      <Icon icon="i-lucide-info" class="inline-block mr-1" />
      {{ $t('CAMPAIGN.BUHOTEXT.CONTACT_SELECTOR.HELP') }}
    </div>
  </div>
</template>
